import { Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import {
  ChevronDown, Menu, X,
  Building2, Briefcase, ShoppingBag, Wallet, Package, Users, Workflow, FileText,
  LayoutGrid, DollarSign, MessagesSquare, BookOpen, HelpCircle, LifeBuoy,
  User, Users2, Building, Crown,
  Handshake, Gift, BadgePercent,
  Info,
} from "lucide-react";

const NAV = [
  {
    label: "Company",
    icon: <Building2 size={16} />,
    items: [{ label: "About Us", icon: <Info size={16} />, href: "/about", desc: "Learn what drives Brivo" }],
  },
  {
    label: "Business",
    icon: <Briefcase size={16} />,
    items: [
      { label: "Sell Online", icon: <ShoppingBag size={16} />, desc: "Get a website, custom domains, run sales", href: "/business/sell-online" },
      { label: "Collect Payments", icon: <Wallet size={16} />, desc: "Receive local & international payments", href: "/business/collect-payments" },
      { label: "Manage Inventory", icon: <Package size={16} />, desc: "Generate barcodes, manage products", href: "/business/manage-inventory" },
      { label: "Manage Operations", icon: <Users size={16} />, desc: "Staff, locations, expenses", href: "/business/manage-operations" },
      { label: "Automate Orders", icon: <Workflow size={16} />, desc: "Stock updates, emails, delivery", href: "/business/automate-orders" },
      { label: "Create Business Records", icon: <FileText size={16} />, desc: "Invoices, receipts, sales records", href: "/business/create-records" },
    ],
  },
  { label: "Modules", icon: <LayoutGrid size={16} />, href: "/modules" },
  { label: "Pricing", icon: <DollarSign size={16} />, href: "/pricing" },
  {
    label: "Partners",
    icon: <Handshake size={16} />,
    items: [
      { label: "Reseller Program", icon: <BadgePercent size={16} />, desc: "Earn up to 45% margin reselling Brivo", href: "/partners/reseller" },
      { label: "Referral Program", icon: <Gift size={16} />, desc: "Refer a friend, both of you earn", href: "/partners/referral" },
    ],
  },
  {
    label: "Explore",
    icon: <BookOpen size={16} />,
    items: [
      { label: "Community", icon: <MessagesSquare size={16} />, desc: "Connect with business owners", href: "/community" },
      { label: "Blog", icon: <BookOpen size={16} />, desc: "Business tips & insights", href: "/blog" },
      { label: "FAQs", icon: <HelpCircle size={16} />, desc: "Common questions answered", href: "/faqs" },
      { label: "Support", icon: <LifeBuoy size={16} />, desc: "Get help anytime", href: "/support" },
    ],
  },
  {
    label: "Who We Serve",
    icon: <Users2 size={16} />,
    items: [
      { label: "For Solopreneurs", icon: <User size={16} />, desc: "Just you, building big", href: "/serve/solopreneurs" },
      { label: "For Medium Businesses", icon: <Briefcase size={16} />, desc: "Scaling without chaos", href: "/serve/medium" },
      { label: "For Large Businesses", icon: <Building size={16} />, desc: "Complex ops, simply", href: "/serve/large" },
      { label: "For Enterprise", icon: <Crown size={16} />, desc: "Custom solutions", href: "/serve/enterprise" },
    ],
  },
];

const Logo = () => (
  <Link to="/" data-testid="brivo-logo" className="flex items-center gap-2 group">
    
    
    <img 
              src="https://brivopos.com/images/site/brivo-logo2.png" 
              alt="Brivo360" 
              className="h-10 w-auto cursor-pointer"
            />
  </Link>
);

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenDropdown(null);
  }, [location.pathname]);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/85 backdrop-blur-xl border-b border-[#12b571]/10 shadow-[0_4px_24px_rgb(27,67,50,0.04)]" : "bg-white/40 backdrop-blur-md"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between gap-4">
        <Logo />

        {/* Desktop nav */}
        <div className="hidden xl:flex items-center gap-0.5">
          {NAV.map((item) => {
            if (item.items) {
              return (
                <div
                  key={item.label}
                  className="relative"
                  onMouseEnter={() => setOpenDropdown(item.label)}
                  onMouseLeave={() => setOpenDropdown(null)}
                >
                  <button
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}-trigger`}
                    className="flex items-center gap-1 px-2.5 py-2 text-sm font-medium text-black/80 hover:text-black rounded-lg transition-colors whitespace-nowrap"
                  >
                    <span className="text-black/60">{item.icon}</span>
                    {item.label}
                    <ChevronDown size={12} className={`transition-transform ${openDropdown === item.label ? "rotate-180" : ""}`} />
                  </button>
                  {openDropdown === item.label && (
                    <div
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}-dropdown`}
                      className="absolute top-full left-0 pt-3 min-w-[340px]"
                    >
                      <div className="glass-strong rounded-2xl p-3 shadow-[0_24px_48px_-8px_rgba(27,67,50,0.18)] border border-[#12b571]/8 animate-fade-in">
                        {item.items.map((sub) => (
                          <Link
                            key={sub.href}
                            to={sub.href}
                            data-testid={`nav-link-${sub.label.toLowerCase().replace(/\s+/g, '-')}`}
                            className="flex items-start gap-3 px-3 py-2.5 rounded-xl hover:bg-[#FFF9E6] transition-colors group"
                          >
                            <div className="w-9 h-9 rounded-lg bg-[#FFF9E6] flex items-center justify-center text-black flex-shrink-0 group-hover:bg-[#12b571] group-hover:text-white transition-colors">
                              {sub.icon}
                            </div>
                            <div className="min-w-0">
                              <div className="font-semibold text-black text-sm">{sub.label}</div>
                              {sub.desc && <div className="text-xs text-black/60 mt-0.5">{sub.desc}</div>}
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            }
            return (
              <Link
                key={item.label}
                to={item.href}
                data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`flex items-center gap-1 px-2.5 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                  location.pathname === item.href ? "text-black" : "text-black/80 hover:text-black"
                }`}
              >
                <span className="text-black/60">{item.icon}</span>
                {item.label}
              </Link>
            );
          })}
        </div>

        <div className="hidden xl:flex items-center gap-3 flex-shrink-0">
          {/* <Link to="/login" data-testid="login-button" className="text-sm font-medium text-black hover:text-[#133124] px-3 py-2">
            Login
          </Link> */}
          <Link to="https://brivopos.com/" data-testid="get-started-button" className="btn-primary text-sm">
            Get Started
          </Link>
        </div>

        {/* Mobile toggle */}
        <button
          data-testid="mobile-menu-toggle"
          className="xl:hidden p-2 text-black"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      {/* Mobile menu */}
      {mobileOpen && (
        <div data-testid="mobile-menu" className="xl:hidden bg-white border-t border-[#12b571]/10 max-h-[80vh] overflow-y-auto">
          <div className="px-6 py-4 space-y-1">
            {NAV.map((item) => (
              <div key={item.label}>
                {item.items ? (
                  <details className="group">
                    <summary className="flex justify-between items-center py-3 cursor-pointer font-medium text-black">
                      <span className="flex items-center gap-2"><span className="text-black/60">{item.icon}</span>{item.label}</span>
                      <ChevronDown size={16} className="group-open:rotate-180 transition-transform" />
                    </summary>
                    <div className="pl-4 pb-2 space-y-1">
                      {item.items.map((s) => (
                        <Link key={s.href} to={s.href} className="flex items-center gap-2 py-2 text-sm text-black/80">
                          <span className="text-black/50">{s.icon}</span>
                          {s.label}
                        </Link>
                      ))}
                    </div>
                  </details>
                ) : (
                  <Link to={item.href} className="flex items-center gap-2 py-3 font-medium text-black">
                    <span className="text-black/60">{item.icon}</span>
                    {item.label}
                  </Link>
                )}
              </div>
            ))}
            <div className="flex flex-col gap-2 pt-4 border-t border-[#12b571]/10 mt-4">
              {/* <Link to="/login" className="btn-secondary justify-center">Login</Link> */}
              <Link to="https://brivopos.com" className="btn-primary justify-center">Get Started</Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
